import React, { useState, useEffect, useCallback } from "react";
import { BASE_URL } from "../config";

const ACTION_BUTTONS = [
    /*
    {
        id: "refresh-tracking",
        label: "Odśwież statusy trackingów",
        description: "Pobiera aktualne statusy dla wszystkich numerów śledzenia.",
        endpoint: "/api/dev/refresh-tracking",
        successMsg: (d) => `Zaktualizowano ${d.updated} zamówień.`,
    },
    {
        id: "sync-shopify",
        label: "Pobierz nowe ordery Shopify",
        description: "Synchronizuje nowe zamówienia ze sklepów Shopify.",
        endpoint: "/api/dev/sync-shopify",
        successMsg: (d) => `Pobrano ${d.new_orders} nowych zamówień.`,
    },
    {
        id: "backfill-sizes",
        label: "Uzupełnij rozmiary (backfill)",
        description: "Przeszukuje wszystkie zamówienia Shopify i uzupełnia brakujące rozmiary.",
        endpoint: "/api/dev/backfill-sizes",
        successMsg: (d) => `Zaktualizowano ${d.updated_sizes} rozmiarów.`,
    },
    {
        id: "backfill-images",
        label: "Uzupełnij zdjęcia (backfill)",
        description: "Pobiera zdjęcia produktów ze Shopify dla zamówień bez zdjęć.",
        endpoint: "/api/dev/backfill-images",
        successMsg: (d) => `Uzupełniono ${d.updated_images} zdjęć.`,
    },
    {
        id: "full-resync",
        label: "Pełny resync Shopify",
        description: "Pobiera WSZYSTKIE zamówienia ze Shopify i zapisuje brakujące. Użyj gdy brakuje starszych orderów.",
        endpoint: "/api/dev/full-resync",
        successMsg: (d) => `Zapisano ${d.new_orders} brakujących zamówień.`,
    },
    */
];

function ActionButton({ button, disabled, onResult }) {
    const [loading, setLoading] = useState(false);

    const handleClick = async () => {
        setLoading(true);
        onResult(button.id, null);
        try {
            const res = await fetch(`${BASE_URL}${button.endpoint}`, {
                method: "POST",
                credentials: "include",
            });
            const data = await res.json();
            if (res.ok) {
                onResult(button.id, { ok: true, message: button.successMsg(data) });
            } else {
                onResult(button.id, { ok: false, message: data.error || "Błąd serwera." });
            }
        } catch {
            onResult(button.id, { ok: false, message: "Błąd połączenia z serwerem." });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ marginBottom: 8 }}>
            <button
                className="btn btn-dark"
                style={{ minWidth: 260, fontSize: 14 }}
                onClick={handleClick}
                disabled={loading || disabled}
            >
                {loading ? (
                    <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" />
                        Ładowanie...
                    </>
                ) : button.label}
            </button>
            <div style={{ fontSize: 12, color: "#888", marginTop: 3 }}>{button.description}</div>
        </div>
    );
}

export default function DevPanel() {
    const [authenticated, setAuthenticated] = useState(false);
    const [checkingAuth, setCheckingAuth] = useState(true);
    const [password, setPassword] = useState("");
    const [loginError, setLoginError] = useState("");
    const [loginLoading, setLoginLoading] = useState(false);
    const [results, setResults] = useState({});
    const [anyLoading, setAnyLoading] = useState(false);

    const checkAuth = useCallback(async () => {
        try {
            const res = await fetch(`${BASE_URL}/api/dev/check-auth`, { credentials: "include" });
            setAuthenticated(res.ok);
        } catch {
            setAuthenticated(false);
        } finally {
            setCheckingAuth(false);
        }
    }, []);

    useEffect(() => { checkAuth(); }, [checkAuth]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginLoading(true);
        setLoginError("");
        try {
            const res = await fetch(`${BASE_URL}/api/dev/login`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ password }),
            });
            const data = await res.json();
            if (res.ok) {
                setAuthenticated(true);
            } else {
                setLoginError(data.error || "Błąd logowania.");
            }
        } catch {
            setLoginError("Błąd połączenia z serwerem.");
        } finally {
            setLoginLoading(false);
        }
    };

    const handleLogout = async () => {
        await fetch(`${BASE_URL}/api/dev/logout`, { method: "POST", credentials: "include" });
        setAuthenticated(false);
        setPassword("");
        setResults({});
    };

    const handleResult = (id, result) => {
        setResults((prev) => ({ ...prev, [id]: result }));
    };

    if (checkingAuth) {
        return <div style={{ textAlign: "center", marginTop: 120, color: "#aaa" }}>Ładowanie...</div>;
    }

    if (!authenticated) {
        return (
            <div style={{ maxWidth: 360, margin: "100px auto", padding: "32px 24px", background: "#fff", borderRadius: 10, boxShadow: "0 2px 16px rgba(0,0,0,0.10)" }}>
                <div style={{ textAlign: "center", marginBottom: 24 }}>
                    <span style={{ fontSize: 28 }}>🔒</span>
                    <h5 style={{ marginTop: 8, fontWeight: 700 }}>Panel developerski</h5>
                    <div style={{ fontSize: 13, color: "#888" }}>Wymagane hasło dostępu</div>
                </div>
                <form onSubmit={handleLogin}>
                    <input
                        type="password"
                        className="form-control"
                        placeholder="Hasło"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoFocus
                        style={{ marginBottom: 12 }}
                    />
                    {loginError && (
                        <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{loginError}</div>
                    )}
                    <button
                        type="submit"
                        className="btn btn-dark w-100"
                        disabled={loginLoading || !password}
                    >
                        {loginLoading ? "Logowanie..." : "Wejdź"}
                    </button>
                </form>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: 560, margin: "60px auto", padding: "0 16px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
                <div>
                    <h4 style={{ fontWeight: 700, marginBottom: 2 }}>Panel developerski</h4>
                    <div style={{ fontSize: 13, color: "#888" }}>Narzędzia administracyjne</div>
                </div>
                <button className="btn btn-outline-secondary btn-sm" onClick={handleLogout}>
                    Wyloguj
                </button>
            </div>

            <div style={{ background: "#fff", borderRadius: 10, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", padding: "24px 28px" }}>
                {ACTION_BUTTONS.map((btn) => (
                    <div key={btn.id}>
                        <ActionButton
                            button={btn}
                            disabled={anyLoading}
                            onResult={(id, result) => {
                                handleResult(id, result);
                                setAnyLoading(false);
                            }}
                        />
                        {results[btn.id] && (
                            <div
                                className={`alert py-2 mb-2 ${results[btn.id].ok ? "alert-success" : "alert-danger"}`}
                                style={{ fontSize: 13 }}
                            >
                                {results[btn.id].message}
                            </div>
                        )}
                        {btn !== ACTION_BUTTONS[ACTION_BUTTONS.length - 1] && <hr style={{ margin: "16px 0" }} />}
                    </div>
                ))}
            </div>
        </div>
    );
}
